import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Cpu, ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react'
import { useAppStore } from '../../store/useAppStore'
import { adminApi } from './adminApi'

interface FeatureSummary {
  feature: string
  provider: string
  calls: number
  cost_thb: number
}

interface DailyEntry {
  date: string
  calls: number
  cost_thb: number
}

interface LogEntry {
  id: string
  feature: string
  provider: string
  model: string | null
  calls: number
  cost_thb: number
  user_id: string | null
  created_at: string
}

interface ApiCostsData {
  summary: {
    total_calls: number
    total_cost_thb: number
    by_feature: FeatureSummary[]
  }
  daily: DailyEntry[]
  recent_logs: LogEntry[]
}

const FEATURE_LABELS: Record<string, string> = {
  dream: 'dream',
  slip: 'slip',
  horoscope: 'horoscope',
}

const FEATURE_BADGES: Record<string, { bg: string; color: string }> = {
  dream: { bg: '#f0e0e8', color: '#9a7a8e' },
  slip: { bg: '#e0eaf0', color: '#5a7a9a' },
  horoscope: { bg: '#e8eedc', color: '#7a8a6a' },
}

function formatMonth(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

function displayMonth(month: string): string {
  const [y, m] = month.split('-')
  const d = new Date(Number(y), Number(m) - 1)
  return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'long' })
}

export default function ApiCostsPage() {
  const { lineUid } = useAppStore()
  const [month, setMonth] = useState(() => formatMonth(new Date()))

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'api-costs', lineUid, month],
    queryFn: () => adminApi.apiCosts(lineUid!, month) as Promise<ApiCostsData>,
    enabled: !!lineUid,
    refetchInterval: 30_000,
  })

  function shiftMonth(delta: number) {
    const [y, m] = month.split('-').map(Number)
    const d = new Date(y, m - 1 + delta)
    setMonth(formatMonth(d))
  }

  const maxDailyCost = Math.max(...(data?.daily ?? []).map(d => d.cost_thb), 0.001)

  return (
    <div className="p-4 md:p-6 lg:p-8">

      {/* Header + Month Picker */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Cpu size={22} style={{ color: '#b8945a' }} />
          <h1 className="text-xl md:text-2xl font-bold" style={{ color: '#2d2820' }}>
            API Costs
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftMonth(-1)}
                  className="p-2 rounded-xl transition-colors hover:bg-black/5"
                  style={{ color: '#6b6354' }}>
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm font-semibold min-w-[120px] text-center"
                style={{ color: '#2d2820' }}>
            {displayMonth(month)}
          </span>
          <button onClick={() => shiftMonth(1)}
                  className="p-2 rounded-xl transition-colors hover:bg-black/5"
                  style={{ color: '#6b6354' }}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Total Cost Card */}
      <div className="mb-6 rounded-3xl p-6 md:p-8 relative overflow-hidden"
           style={{
             background: 'linear-gradient(135deg, #d4b884 0%, #b8945a 100%)',
             boxShadow: '0 8px 24px rgba(184, 148, 90, 0.3)',
           }}>
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-widest text-white/80 mb-1">
            Total API Cost
          </div>
          <div className="text-3xl md:text-4xl font-bold text-white mb-1">
            {isLoading ? '...' : `${data?.summary.total_cost_thb.toFixed(3) ?? '0.000'}฿`}
          </div>
          <div className="text-sm text-white/90">
            {isLoading ? '' : `${data?.summary.total_calls ?? 0} calls`}
          </div>
        </div>
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-20"
             style={{ background: '#fff' }} />
        <div className="absolute -right-4 bottom-0 w-32 h-32 rounded-full opacity-10"
             style={{ background: '#fff' }} />
      </div>

      {/* Feature Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-6">
        {(['dream', 'slip', 'horoscope'] as const).map(feat => {
          const item = data?.summary.by_feature.find(f => f.feature === feat)
          const badge = FEATURE_BADGES[feat]
          return (
            <div key={feat}
                 className="rounded-2xl p-4 md:p-5"
                 style={{ background: '#faf7ef', border: '1px solid rgba(0,0,0,0.04)' }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                      style={{ background: badge.bg, color: badge.color }}>
                  {FEATURE_LABELS[feat]}
                </span>
                <span className="text-[10px]" style={{ color: '#8a8270' }}>
                  {item?.provider ?? 'gemini'}
                </span>
              </div>
              <div className="text-2xl font-bold mb-0.5" style={{ color: '#2d2820' }}>
                {isLoading ? '...' : `${(item?.cost_thb ?? 0).toFixed(3)}฿`}
              </div>
              <div className="text-[11px]" style={{ color: '#6b6354' }}>
                {isLoading ? '' : `${item?.calls ?? 0} calls`}
              </div>
            </div>
          )
        })}
      </div>

      {/* Daily Bar Chart */}
      <div className="rounded-2xl p-4 md:p-6 mb-6"
           style={{ background: '#faf7ef', border: '1px solid rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} style={{ color: '#b8945a' }} />
          <h2 className="font-bold text-base" style={{ color: '#2d2820' }}>
            Daily Cost
          </h2>
        </div>

        {isLoading ? (
          <div className="h-32 flex items-center justify-center text-sm"
               style={{ color: '#8a8270' }}>
            ...
          </div>
        ) : !data?.daily.length ? (
          <div className="h-32 flex items-center justify-center text-sm"
               style={{ color: '#8a8270' }}>
            No data
          </div>
        ) : (
          <div className="flex items-end gap-px md:gap-1 h-32 md:h-36">
            {data.daily.map((day) => {
              const heightPercent = (day.cost_thb / maxDailyCost) * 100
              const d = new Date(day.date)
              const label = `${d.getDate()}`
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
                  <div className="hidden md:block text-[9px] font-bold truncate" style={{ color: '#b8945a' }}>
                    {day.cost_thb > 0 ? day.cost_thb.toFixed(3) : ''}
                  </div>
                  <div className="w-full rounded-t-md flex items-end"
                       style={{ background: 'rgba(184, 148, 90, 0.1)', height: '100%' }}>
                    <div className="w-full rounded-t-md transition-all"
                         style={{
                           background: 'linear-gradient(180deg, #d4b884 0%, #b8945a 100%)',
                           height: `${heightPercent}%`,
                           minHeight: day.cost_thb > 0 ? '3px' : '0',
                         }} />
                  </div>
                  <div className="text-[7px] md:text-[9px]" style={{ color: '#8a8270' }}>
                    {label}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Recent Logs */}
      <div className="rounded-2xl overflow-hidden"
           style={{ background: '#faf7ef', border: '1px solid rgba(0,0,0,0.04)' }}>
        <div className="px-4 md:px-5 py-3 md:py-4 flex items-center gap-2"
             style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <h2 className="font-bold text-base" style={{ color: '#2d2820' }}>
            Recent Logs
          </h2>
          <span className="text-xs" style={{ color: '#8a8270' }}>
            (50)
          </span>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-sm" style={{ color: '#8a8270' }}>...</div>
        ) : !data?.recent_logs.length ? (
          <div className="p-8 text-center text-sm" style={{ color: '#8a8270' }}>No logs yet</div>
        ) : (
          data.recent_logs.map((log) => {
            const badge = FEATURE_BADGES[log.feature] ?? { bg: '#eee', color: '#666' }
            const time = new Date(log.created_at)
            return (
              <div key={log.id}
                   className="flex items-center gap-3 px-4 py-3 border-b last:border-0"
                   style={{ borderColor: 'rgba(0,0,0,0.04)' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{ background: badge.bg, color: badge.color }}>
                      {log.feature}
                    </span>
                    <span className="text-[10px] truncate" style={{ color: '#8a8270' }}>
                      {log.model ?? '-'}
                    </span>
                  </div>
                  <div className="text-[10px]" style={{ color: '#a89e88' }}>
                    {time.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}{' '}
                    {time.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold" style={{ color: '#b8945a' }}>
                    {log.cost_thb.toFixed(3)}฿
                  </div>
                  <div className="text-[10px]" style={{ color: '#6b6354' }}>
                    {log.calls} call{log.calls > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
